import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { X, Minus, Plus, ShoppingBag, ArrowRight, Trash2, MessageCircle } from "lucide-react";
import { resolveImageUrl } from "../lib/imageUrl";
import ProductInquiryDialog from "./ProductInquiryDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CartDrawer({ open, onClose, sessionId, onCartUpdate }) {
  const [cart, setCart] = useState({ items: [] });
  const [loading, setLoading] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const navigate = useNavigate();

  const fetchCart = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/cart/${sessionId}`);
      setCart(res.data);
    } catch (err) {
      console.error("Error fetching cart:", err);
    }
  }, [sessionId]);

  useEffect(() => {
    if (open && sessionId) {
      fetchCart();
    }
  }, [open, sessionId, fetchCart]);

  const updateQuantity = async (productId, quantity, variant) => {
    setLoading(true);
    try {
      const url = `${API}/cart/${sessionId}/item/${productId}${variant ? `?variant=${encodeURIComponent(variant)}` : ''}`;
      await axios.patch(url, { quantity });
      await fetchCart();
      onCartUpdate?.();
    } catch (err) {
      toast.error("Failed to update quantity");
    } finally {
      setLoading(false);
    }
  };

  const removeItem = async (productId, variant) => {
    setLoading(true);
    try {
      const url = `${API}/cart/${sessionId}/item/${productId}${variant ? `?variant=${encodeURIComponent(variant)}` : ''}`;
      await axios.delete(url);
      await fetchCart();
      onCartUpdate?.();
      toast.success("Item removed from cart");
    } catch (err) {
      toast.error("Failed to remove item");
    } finally {
      setLoading(false);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(price);
  };

  const subtotal = cart.items?.reduce((sum, item) => sum + (item.price * item.quantity), 0) || 0;
  const tax = subtotal * 0.08;
  const shipping = subtotal > 0 ? 25 : 0;
  const total = subtotal + tax + shipping;

  const handleCheckout = () => {
    onClose();
    navigate('/checkout');
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100]" data-testid="cart-drawer">
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Drawer */}
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-[#0a0a0b] border-l border-white/10 flex flex-col animate-slide-in-right">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div className="flex items-center gap-3">
            <ShoppingBag className="w-6 h-6 text-primary" />
            <h2 className="text-xl font-light text-[#F5F5F0]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Your Cart
            </h2>
            <span className="text-sm text-[#71717A]">({cart.items?.length || 0} items)</span>
          </div>
          <button 
            onClick={onClose}
            className="p-2 hover:bg-white/5 transition-colors"
            data-testid="close-cart"
          >
            <X className="w-5 h-5 text-[#A1A1AA]" />
          </button>
        </div>

        {/* Cart Items */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {cart.items?.length === 0 ? (
            <div className="text-center py-12">
              <ShoppingBag className="w-16 h-16 text-[#71717A] mx-auto mb-4 opacity-50" />
              <p className="text-[#71717A] mb-6">Your cart is empty</p>
              <Link 
                to="/shop" 
                onClick={onClose}
                className="btn-primary inline-flex items-center gap-2"
                data-testid="continue-shopping"
              >
                Continue Shopping
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          ) : (
            cart.items?.map((item, idx) => (
              <div 
                key={`${item.product_id}-${item.selected_gem_variant || idx}`}
                className="flex gap-4 p-4 bg-[#121214] border border-white/5"
                data-testid={`cart-item-${item.product_id}`}
              >
                <div className="w-20 h-20 flex-shrink-0 overflow-hidden">
                  <img 
                    src={resolveImageUrl(item.product_image_url)} 
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-[#F5F5F0] truncate">{item.title}</h3>
                  {item.selected_gem_variant && (
                    <p className="text-xs text-primary mt-1">{item.selected_gem_variant}</p>
                  )}
                  <p className="text-sm text-[#A1A1AA] mt-1">{formatPrice(item.price)}</p>
                  
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center border border-white/10">
                      <button 
                        onClick={() => updateQuantity(item.product_id, item.quantity - 1, item.selected_gem_variant)}
                        disabled={loading || item.quantity <= 1}
                        className="p-1.5 hover:bg-white/5 transition-colors disabled:opacity-50"
                        data-testid={`decrease-${item.product_id}`}
                      >
                        <Minus className="w-3 h-3 text-[#A1A1AA]" />
                      </button>
                      <span className="px-3 text-sm text-[#F5F5F0]" data-testid={`quantity-${item.product_id}`}>{item.quantity}</span>
                      <button 
                        onClick={() => updateQuantity(item.product_id, item.quantity + 1, item.selected_gem_variant)}
                        disabled={loading}
                        className="p-1.5 hover:bg-white/5 transition-colors disabled:opacity-50"
                        data-testid={`increase-${item.product_id}`}
                      >
                        <Plus className="w-3 h-3 text-[#A1A1AA]" />
                      </button>
                    </div>
                    <button 
                      onClick={() => removeItem(item.product_id, item.selected_gem_variant)}
                      disabled={loading}
                      className="p-1.5 hover:bg-white/5 transition-colors text-red-500"
                      data-testid={`remove-${item.product_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        {cart.items?.length > 0 && (
          <div className="border-t border-white/10 p-6 space-y-4">
            <div className="space-y-2 text-sm">
              <div className="flex justify-between text-[#A1A1AA]">
                <span>Subtotal</span>
                <span data-testid="cart-subtotal">{formatPrice(subtotal)}</span>
              </div>
              <div className="flex justify-between text-[#A1A1AA]">
                <span>Estimated Tax (8%)</span>
                <span data-testid="cart-tax">{formatPrice(tax)}</span>
              </div>
              <div className="flex justify-between text-[#A1A1AA]">
                <span>Shipping</span>
                <span data-testid="cart-shipping">{formatPrice(shipping)}</span>
              </div>
              <div className="flex justify-between text-[#F5F5F0] text-lg pt-2 border-t border-white/10">
                <span>Total</span>
                <span className="text-primary" data-testid="cart-total">{formatPrice(total)}</span>
              </div>
            </div>
            
            <button 
              onClick={handleCheckout}
              className="btn-primary w-full flex items-center justify-center gap-2"
              data-testid="checkout-button"
            >
              Proceed to Checkout
              <ArrowRight className="w-4 h-4" />
            </button>

            <button
              onClick={() => setInquiryOpen(true)}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-[#C9A962]/40 text-[#C9A962] hover:bg-[#C9A962]/10 transition-all text-xs uppercase tracking-wider"
              data-testid="cart-inquire-btn"
            >
              <MessageCircle className="w-4 h-4" />
              Enquire about these items
            </button>
            
            <Link 
              to="/shop" 
              onClick={onClose}
              className="block text-center text-sm text-[#A1A1AA] hover:text-primary transition-colors"
              data-testid="continue-shopping-link"
            >
              Continue Shopping
            </Link>
          </div>
        )}
      </div>

      <ProductInquiryDialog
        open={inquiryOpen}
        onClose={() => setInquiryOpen(false)}
        cartItems={cart.items}
      />

      <style>{`
        @keyframes slide-in-right {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
